import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { PartnerSearchSsoUsersV1Params, PartnerUser } from '@shipium-corp/shipium-ts-types';
import type { User } from '~/store/services/AuthService/types';
import type { UserUpdateRequest } from '~/store/services/PartnerService/types';

const USERS_PROXY = '/partner-management-proxy/api/v1/users';

export const users = {
  search: {
    queryKey: 'partner-management-service/users',
    fn: async <
      T extends
        | PartnerSearchSsoUsersV1Params
        | {
            includeInactive?: boolean;
            principal?: string;
          },
    >(
      partnerId: string,
      params: T = {} as T,
    ) => {
      const { data, headers } = await GatewayAxiosClient.get<User[]>(`/partners/${partnerId}/users`, {
        params,
      });

      return {
        data,
        pageNext: headers['x-page-next'],
        pagePrevious: headers['x-page-previous'],
        totalCount: Number(headers['x-total-count']) || 0,
      };
    },
  },
  searchNonSso: {
    queryKey: 'partner-management-service/users/non-sso',
    fn: async () => {
      const { data } = await GatewayAxiosClient.get<PartnerUser[]>(`/partner-management-proxy/api/v1/users`);
      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/users/id',
    configurationType: 'no-cache' as const,
    fn: async (userId: string): Promise<PartnerUser> => {
      const [user, sso] = await Promise.allSettled([
        GatewayAxiosClient.get(`/partner-management-proxy/api/v1/users/${userId}`),
        GatewayAxiosClient.get(`/partner-management-proxy/api/v1/users/${userId}/sso`),
      ]);

      if (sso.status === 'fulfilled' && sso.value.data) {
        return sso.value.data;
      }

      if (user.status === 'fulfilled' && user.value.data) {
        return user.value.data;
      }

      throw new Error('User not found!');
    },
  },
  // Replaces the gateway `updateUser` GraphQL mutation: a partner-link role update followed by a
  // profile patch. Mirrors the resolver, including stitching sessionPartnerRole onto the response
  // (the PATCH response does not carry it).
  update: async (request: UserUpdateRequest): Promise<User> => {
    await GatewayAxiosClient.post(`${USERS_PROXY}/${request.userId}/linkPartner`, {
      partnerId: request.sessionPartnerId,
      role: request.sessionPartnerRole,
      type: 'update',
    });
    const { data } = await GatewayAxiosClient.patch<User>(`${USERS_PROXY}/${request.userId}`, {
      givenName: request.givenName,
      surname: request.surname,
      passwordExpirationDateTime: request.passwordExpirationDateTime ?? null,
    });
    return { ...data, sessionPartnerRole: request.sessionPartnerRole };
  },
  // Replaces the gateway `unlinkUser` GraphQL mutation. The API requires a role even when unlinking.
  unlink: async ({ partnerId, userId }: { partnerId: string; userId: string }): Promise<void> => {
    await GatewayAxiosClient.post(`${USERS_PROXY}/${userId}/linkPartner`, {
      partnerId,
      role: 'editor',
      type: 'unlink',
    });
  },
};
